import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import DashboardAIAssistant from '../components/dashboard/DashboardAIAssistant';
import MeetingSummaryModal from '../components/meeting/MeetingSummaryModal';
import {
  Video,
  Plus,
  Users,
  Clock,
  LogOut,
  Settings,
  CalendarDays,
  Link2,
  Copy,
  Trash2,
  MoreVertical,
  Play,
  ArrowRight,
  Download,
  Sparkles,
  FileText,
  Briefcase,
  GraduationCap
} from 'lucide-react';

// Meeting modes — tailor the experience for different contexts.
// Full static Tailwind class strings (dynamic `bg-${x}` names get purged).
const MEETING_TYPES = [
  {
    value: 'general', label: 'General', icon: Video,
    activeClass: 'border-neon-cyan/60 bg-neon-cyan/10 text-white',
    iconActive: 'text-neon-cyan', badge: 'bg-neon-cyan/15 text-neon-cyan',
  },
  {
    value: 'office', label: 'Office', icon: Briefcase,
    activeClass: 'border-neon-blue/60 bg-neon-blue/10 text-white',
    iconActive: 'text-neon-blue', badge: 'bg-neon-blue/15 text-neon-blue',
  },
  {
    value: 'school', label: 'School', icon: GraduationCap,
    activeClass: 'border-neon-purple/60 bg-neon-purple/10 text-white',
    iconActive: 'text-neon-purple', badge: 'bg-neon-purple/15 text-neon-purple',
  },
];

const getMeetingType = (value) =>
  MEETING_TYPES.find((t) => t.value === value) || MEETING_TYPES[0];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', description: '', scheduledAt: '', meetingType: 'general' });
  const [joinRoomId, setJoinRoomId] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [notesModal, setNotesModal] = useState({ open: false, loading: false, summary: null, roomName: '' });

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await api.get('/rooms/my');
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoom.name.trim()) {
      toast.error('Please enter a meeting name');
      return;
    }

    // Validate the scheduled time is in the future.
    let scheduledIso = null;
    let isFutureSchedule = false;
    if (newRoom.scheduledAt) {
      const scheduledDate = new Date(newRoom.scheduledAt);
      if (isNaN(scheduledDate.getTime())) {
        toast.error('Please enter a valid date and time');
        return;
      }
      scheduledIso = scheduledDate.toISOString();
      // Joining opens 5 minutes before the scheduled time.
      isFutureSchedule = scheduledDate.getTime() - Date.now() > 300000;
    }

    try {
      const response = await api.post('/rooms', {
        name: newRoom.name,
        description: newRoom.description,
        scheduled_at: scheduledIso,
        meeting_type: newRoom.meetingType,
      });

      setCreateDialogOpen(false);
      setNewRoom({ name: '', description: '', scheduledAt: '', meetingType: 'general' });
      fetchRooms();

      if (isFutureSchedule) {
        // Don't drop the host into a meeting that hasn't started yet —
        // it now lives in "My Meetings" and can be joined near its time.
        toast.success('Meeting scheduled! It will appear in My Meetings.');
      } else {
        toast.success('Meeting created!');
        navigate(`/meeting/${response.data.room_id}`);
      }
    } catch (error) {
      toast.error('Failed to create meeting');
    }
  };

  const handleJoinRoom = () => {
    if (!joinRoomId.trim()) {
      toast.error('Please enter a meeting ID');
      return;
    }

    // Extract room_id if full URL is pasted
    let roomId = joinRoomId.trim();
    if (roomId.includes('/meeting/')) {
      roomId = roomId.split('/meeting/')[1].split('?')[0];
    }

    navigate(`/meeting/${roomId}`);
  };

  const attemptJoinScheduledRoom = (room) => {
    if (room.scheduled_at) {
      const scheduledTime = new Date(room.scheduled_at);
      const now = new Date();
      const diffMs = scheduledTime - now;
      // 5 minutes in ms = 300,000
      if (diffMs > 300000) {
        toast.error(`Too early! This meeting starts at ${format(scheduledTime, "p")}. You can join 5 minutes before.`);
        return;
      }
    }
    navigate(`/meeting/${room.room_id}`);
  };

  const handleDeleteRoom = async (roomId) => {
    try {
      await api.delete(`/rooms/${roomId}`);
      toast.success('Meeting deleted');
      fetchRooms();
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const copyMeetingLink = (roomId) => {
    const link = `${window.location.origin}/meeting/${roomId}`;
    navigator.clipboard.writeText(link);
    toast.success('Meeting link copied!');
  };

  const viewMeetingNotes = async (room) => {
    setNotesModal({ open: true, loading: true, summary: null, roomName: room.name });
    try {
      const response = await api.get(`/rooms/${room.room_id}/summary`);
      setNotesModal({ open: true, loading: false, summary: response.data, roomName: room.name });
    } catch (error) {
      const detail = error.response?.status === 404
        ? 'No AI notes were saved for this meeting yet. Notes are generated from the meeting transcript.'
        : (error.response?.data?.detail || 'Could not load meeting notes.');
      setNotesModal({ open: true, loading: false, summary: { error: detail }, roomName: room.name });
    }
  };

  const startInstantMeeting = async () => {
    try {
      const response = await api.post('/rooms', {
        name: `Quick Meeting - ${new Date().toLocaleTimeString()}`,
        description: 'Instant meeting',
      });
      navigate(`/meeting/${response.data.room_id}`);
    } catch (error) {
      toast.error('Failed to start meeting');
    }
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="border-b border-white/5 bg-void-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="RevealIQ Meet" className="w-14 h-14 object-contain" />
            <span className="font-heading font-bold text-xl text-white hidden sm:block">RevealIQ Meet</span>
          </div>

          <div className="flex items-center gap-4">
            {deferredPrompt && (
              <Button
                onClick={handleInstallClick}
                className="hidden md:flex items-center gap-2 btn-neon bg-neon-green/10 text-neon-green border-neon-green/50 hover:bg-neon-green/20 mr-2"
              >
                <Download className="w-4 h-4" />
                Install App
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-3 hover:bg-white/5" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-neon-cyan/20 text-neon-cyan">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-grey-100 font-body hidden md:block">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-void-paper border border-white/10 text-grey-100">
                <DropdownMenuItem
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => navigate('/settings')}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                {deferredPrompt && (
                  <DropdownMenuItem
                    className="md:hidden hover:bg-white/5 cursor-pointer text-neon-cyan"
                    onClick={handleInstallClick}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Install App
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="hover:bg-white/5 cursor-pointer text-red-400"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-grey-400 font-body">
            Start or join a meeting to connect with your team.
          </p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Instant Meeting */}
          <button
            onClick={startInstantMeeting}
            data-testid="instant-meeting-btn"
            className="glass-card p-6 text-left group hover:border-neon-cyan/30 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-neon-cyan/20 flex items-center justify-center mb-4 group-hover:shadow-neon-cyan transition-shadow">
              <Play className="w-6 h-6 text-neon-cyan" />
            </div>
            <h3 className="font-heading font-semibold text-white mb-1">New Meeting</h3>
            <p className="text-grey-400 text-sm font-body">Start an instant meeting</p>
          </button>

          {/* Join Meeting */}
          <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="join-meeting-btn"
                className="glass-card p-6 text-left group hover:border-neon-magenta/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-magenta/20 flex items-center justify-center mb-4 group-hover:shadow-neon-magenta transition-shadow">
                  <Link2 className="w-6 h-6 text-neon-magenta" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-1">Join Meeting</h3>
                <p className="text-grey-400 text-sm font-body">Join with a code or link</p>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-void-paper border border-white/10 text-grey-100">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Join a Meeting</DialogTitle>
                <DialogDescription className="text-grey-400 text-sm">
                  Enter a meeting code or paste an invite link to join.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <Input
                  placeholder="Enter meeting code or link"
                  value={joinRoomId}
                  onChange={(e) => setJoinRoomId(e.target.value)}
                  className="input-neon"
                  data-testid="join-room-input"
                />
                <Button
                  onClick={handleJoinRoom}
                  className="w-full btn-neon"
                  data-testid="join-room-submit-btn"
                >
                  Join Meeting
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Schedule Meeting */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="schedule-meeting-btn"
                className="glass-card p-6 text-left group hover:border-neon-blue/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-blue/20 flex items-center justify-center mb-4 group-hover:shadow-neon-blue transition-shadow">
                  <CalendarDays className="w-6 h-6 text-neon-blue" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-1">Schedule</h3>
                <p className="text-grey-400 text-sm font-body">Plan a future meeting</p>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-void-paper border border-white/10 text-grey-100">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create a Meeting</DialogTitle>
                <DialogDescription className="text-grey-400 text-sm">
                  Set up a new meeting room for your team.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-grey-400 mb-1 block">Meeting Name</label>
                  <Input
                    placeholder="Team Standup"
                    value={newRoom.name}
                    onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                    className="input-neon"
                    data-testid="create-room-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-grey-400 mb-1 block">Description (optional)</label>
                  <Input
                    placeholder="Weekly sync meeting"
                    value={newRoom.description}
                    onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                    className="input-neon"
                    data-testid="create-room-description-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-grey-400 mb-2 block">Meeting Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {MEETING_TYPES.map((type) => {
                      const Icon = type.icon;
                      const active = newRoom.meetingType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setNewRoom({ ...newRoom, meetingType: type.value })}
                          data-testid={`meeting-type-${type.value}`}
                          className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-all duration-200 ${active
                            ? type.activeClass
                            : 'border-white/10 bg-white/5 text-grey-400 hover:border-white/20'
                            }`}
                        >
                          <Icon className={`w-5 h-5 ${active ? type.iconActive : ''}`} />
                          <span className="text-xs font-medium">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-grey-500 mt-1.5">
                    {newRoom.meetingType === 'office' && 'AI focuses on decisions, action items & owners.'}
                    {newRoom.meetingType === 'school' && 'AI acts as a teaching assistant — explanations, quizzes & notes.'}
                    {newRoom.meetingType === 'general' && 'Balanced AI copilot for any conversation.'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-grey-400 mb-1 block">Schedule (Date & Time)</label>
                  <Input
                    type="datetime-local"
                    value={newRoom.scheduledAt}
                    onChange={(e) => setNewRoom({ ...newRoom, scheduledAt: e.target.value })}
                    className="input-neon [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                    data-testid="schedule-datetime-input"
                  />
                </div>
                <Button
                  onClick={handleCreateRoom}
                  className="w-full btn-neon"
                  data-testid="create-room-submit-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Meeting
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* AI Assistant */}
          <button
            onClick={() => setAiOpen(true)}
            data-testid="ai-assistant-btn"
            className="glass-card p-6 text-left group hover:border-neon-purple/30 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-neon-purple/20 flex items-center justify-center mb-4 group-hover:shadow-neon-purple transition-shadow">
              <Sparkles className="w-6 h-6 text-neon-purple" />
            </div>
            <h3 className="font-heading font-semibold text-white mb-1">AI Assistant</h3>
            <p className="text-grey-400 text-sm font-body">Ask Kautilya about your meetings</p>
          </button>
        </motion.div>

        {/* My Meetings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-semibold text-2xl text-white">My Meetings</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-neon-cyan border-t-transparent rounded-full animate-spin" />
            </div>
          ) : rooms.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Video className="w-8 h-8 text-grey-400" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-white mb-2">No meetings yet</h3>
              <p className="text-grey-400 font-body mb-6">Create your first meeting to get started</p>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="btn-neon"
                data-testid="empty-create-meeting-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Meeting
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="meetings-grid">
              {rooms.map((room) => (
                <motion.div
                  key={room.room_id}
                  className="glass-card p-5 group hover:border-white/20 transition-all duration-300"
                  whileHover={{ y: -2 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-white mb-1 truncate">
                        {room.name}
                      </h3>
                      {room.description && (
                        <p className="text-grey-400 text-sm font-body truncate">
                          {room.description}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-white/5 rounded" data-testid={`room-menu-${room.room_id}`}>
                          <MoreVertical className="w-4 h-4 text-grey-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-void-paper border border-white/10 text-grey-100">
                        <DropdownMenuItem
                          className="hover:bg-white/5 cursor-pointer"
                          onClick={() => copyMeetingLink(room.room_id)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Link
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="hover:bg-white/5 cursor-pointer"
                          onClick={() => viewMeetingNotes(room)}
                          data-testid={`notes-room-${room.room_id}`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View AI Notes
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="hover:bg-white/5 cursor-pointer text-red-400"
                          onClick={() => handleDeleteRoom(room.room_id)}
                          data-testid={`delete-room-${room.room_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex flex-col gap-2 text-grey-400 text-sm mb-4">
                    {(() => {
                      const t = getMeetingType(room.meeting_type);
                      const BadgeIcon = t.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-xs font-medium ${t.badge}`}>
                          <BadgeIcon className="w-3 h-3" />
                          {t.label}
                        </span>
                      );
                    })()}
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-neon-cyan/80" />
                      {room.scheduled_at 
                        ? `Scheduled: ${format(new Date(room.scheduled_at), "MMM d, h:mm a")}` 
                        : `Created: ${new Date(room.created_at).toLocaleDateString()}`
                      }
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {room.participants_count || 0} participants
                    </div>
                  </div>

                  <Button
                    onClick={() => attemptJoinScheduledRoom(room)}
                    className="w-full btn-ghost text-sm py-2"
                    data-testid={`join-room-${room.room_id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Join Meeting
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Always-available AI assistant */}
      <DashboardAIAssistant
        userName={user?.name?.split(' ')[0]}
        isOpen={aiOpen}
        onOpen={() => setAiOpen(true)}
        onClose={() => setAiOpen(false)}
      />

      {/* Saved meeting notes / summary */}
      <MeetingSummaryModal
        isOpen={notesModal.open}
        onClose={() => setNotesModal((s) => ({ ...s, open: false }))}
        summary={notesModal.summary}
        isLoading={notesModal.loading}
        roomName={notesModal.roomName}
      />
    </div>
  );
};

export default Dashboard;
